<template>
  <div class="jobs-page-holder">
    <div class="jobs printers-page">
      <div class="page-header d-flex flex-row align-items-center mb-3">
        <h1 class="m-0 flex-fill font-weight-bold">Printers</h1>
        <small class="text-muted mr-3" v-if="refreshedAt">
          Updated {{ refreshedAt | moment('timezone', timezone, 'h:mm:ss a') }}
        </small>
        <b-button variant="primary" :disabled="loading" @click="refresh">
          <b-spinner small v-if="loading" class="mr-1" />
          Refresh
        </b-button>
      </div>

      <b-alert variant="danger" :show="!!error">{{ error }}</b-alert>

      <b-row class="mb-4" v-if="summary">
        <b-col md="3">
          <b-card class="h-100">
            <div class="text-muted small">Latest version</div>
            <div class="h4 m-0">{{ summary.latest_version || '-' }}</div>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card class="h-100">
            <div class="text-muted small">Computers online</div>
            <div class="h4 m-0">{{ summary.agents.online }} / {{ summary.agents.total }}</div>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card class="h-100">
            <div class="text-muted small">Outdated computers</div>
            <div class="h4 m-0" :class="{ 'text-danger': summary.agents.outdated > 0 }">
              {{ summary.agents.outdated }}
            </div>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card class="h-100">
            <div class="text-muted small">Prints, last 24 hours</div>
            <div class="h4 m-0">{{ summary.jobs_today.total }}</div>
            <div class="small">
              <span class="text-success">{{ summary.jobs_today.printed }} printed</span> ·
              <span class="text-danger">{{ summary.jobs_today.failed }} failed</span> ·
              <span class="text-warning">{{ summary.jobs_today.no_report }} no report</span>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <h4 class="font-weight-bold">Stores</h4>
      <b-table
        :items="outlets"
        :fields="outletFields"
        head-variant="light"
        responsive="sm"
        show-empty
        empty-text="No store has printed yet"
        hover
        small
        class="mb-4"
        :tbody-tr-class="outletRowClass"
        @row-clicked="selectOutlet"
      >
        <template v-slot:cell(versions)="data">
          <span
            v-for="version in data.item.versions"
            :key="version"
            class="mr-2"
            :class="{ 'text-danger font-weight-bold': version !== summary.latest_version }"
            >{{ version }}</span
          >
        </template>
        <template v-slot:cell(last_print_at)="data">
          <span v-if="data.item.last_print_at" :title="absolute(data.item.last_print_at)">
            {{ relative(data.item.last_print_at) }}
          </span>
          <span v-else>-</span>
        </template>
        <template v-slot:cell(failed_24h)="data">
          <span :class="{ 'text-danger font-weight-bold': data.item.failed_24h > 0 }">
            {{ data.item.failed_24h }}
          </span>
        </template>
      </b-table>

      <div class="d-flex align-items-center mb-2" v-if="outletId">
        <span class="mr-2"
          >Showing store: <strong>{{ outletName(outletId) }}</strong></span
        >
        <b-button size="sm" variant="outline-secondary" @click="selectOutlet(null)">
          Show all stores
        </b-button>
      </div>

      <h4 class="font-weight-bold">Computers</h4>
      <b-table
        :items="agents"
        :fields="agentFields"
        head-variant="light"
        responsive="sm"
        show-empty
        empty-text="No computer has reported yet"
        small
        class="mb-4"
      >
        <template v-slot:cell(outlet)="data">
          {{ data.item.outlet ? data.item.outlet.name : '-' }}
        </template>
        <template v-slot:cell(os)="data">{{ osName(data.item.os) }}</template>
        <template v-slot:cell(version)="data">
          {{ data.item.version }}
          <b-badge variant="danger" v-if="!data.item.is_latest">outdated</b-badge>
        </template>
        <template v-slot:cell(printer)="data">
          <div>{{ data.item.printer_name || '-' }}</div>
          <small class="text-muted">
            {{ data.item.printer_address }} {{ data.item.printer_language }}
          </small>
        </template>
        <template v-slot:cell(online)="data">
          <span class="status-dot" :class="data.item.online ? 'online' : 'offline'" />
          <span :title="absolute(data.item.last_seen_at)">{{
            relative(data.item.last_seen_at)
          }}</span>
        </template>
        <template v-slot:cell(last_print)="data">
          <template v-if="data.item.last_print_at">
            <b-badge :variant="data.item.last_print_ok ? 'success' : 'danger'">
              {{ data.item.last_print_ok ? 'ok' : 'failed' }}
            </b-badge>
            <span :title="absolute(data.item.last_print_at)">
              {{ relative(data.item.last_print_at) }}
            </span>
            <div
              v-if="data.item.last_error"
              class="small text-danger text-truncate error-cell"
              :title="data.item.last_error"
            >
              {{ data.item.last_error }}
            </div>
          </template>
          <span v-else>-</span>
        </template>
      </b-table>

      <h4 class="font-weight-bold">Prints</h4>
      <div class="d-flex flex-wrap align-items-center mb-2">
        <b-form-select
          v-model="outletId"
          :options="outletOptions"
          class="mr-2 mb-2 filter-select"
          @change="loadJobsFirstPage"
        />
        <b-form-select
          v-model="status"
          :options="statusOptions"
          class="mr-2 mb-2 filter-select"
          @change="loadJobsFirstPage"
        />
        <b-form-input
          v-model="orderSearch"
          placeholder="Order number"
          class="mr-2 mb-2 filter-select"
          @keyup.enter="loadJobsFirstPage"
        />
        <b-button class="mb-2" variant="outline-primary" @click="loadJobsFirstPage">
          Search
        </b-button>
      </div>
      <b-table
        :items="jobs"
        :fields="jobFields"
        head-variant="light"
        responsive="sm"
        show-empty
        empty-text="No prints found"
        hover
        small
        class="clickable-rows"
        @row-clicked="openJob"
      >
        <template v-slot:cell(created_at)="data">
          <span :title="relative(data.item.created_at)">{{ absolute(data.item.created_at) }}</span>
        </template>
        <template v-slot:cell(outlet)="data">
          {{ data.item.outlet ? data.item.outlet.name : '-' }}
        </template>
        <template v-slot:cell(invoice)="data">{{
          data.item.invoice || `#${data.item.order_id}`
        }}</template>
        <template v-slot:cell(agent)="data">
          <template v-if="data.item.agent">
            <div>{{ data.item.agent.hostname }}</div>
            <small class="text-muted">{{
              data.item.agent_version || data.item.agent.version
            }}</small>
          </template>
          <small v-else class="text-muted">{{ data.item.agent_version || 'old agent' }}</small>
        </template>
        <template v-slot:cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ statusLabel(data.item.status) }}
          </b-badge>
        </template>
        <template v-slot:cell(bytes)="data">{{ kb(data.item.bytes) }}</template>
        <template v-slot:cell(timings)="data">
          <small>{{ timings(data.item) }}</small>
        </template>
        <template v-slot:cell(error)="data">
          <div class="small text-danger text-truncate error-cell" :title="data.item.error">
            {{ data.item.error }}
          </div>
        </template>
      </b-table>
      <b-pagination
        v-if="totalJobs > perPage"
        v-model="page"
        :total-rows="totalJobs"
        :per-page="perPage"
        align="center"
        @change="loadJobsPage"
      />

      <b-modal v-model="showJob" size="lg" title="Print details" hide-footer>
        <div v-if="jobLoading" class="text-center py-4"><b-spinner /></div>
        <template v-else-if="job">
          <dl class="row mb-3">
            <template v-for="row in jobRows">
              <dt class="col-sm-4" :key="`${row.label}-label`">{{ row.label }}</dt>
              <dd class="col-sm-8" :key="`${row.label}-value`">{{ row.value }}</dd>
            </template>
          </dl>
          <h6 class="font-weight-bold">Agent log</h6>
          <pre class="log-tail">{{ job.log_tail || 'No log was sent with this print.' }}</pre>
        </template>
      </b-modal>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const OS_NAMES = {
  'windows/amd64': 'Windows 64-bit',
  'windows/386': 'Windows 32-bit',
  'darwin/arm64': 'Mac (Apple)',
  'darwin/amd64': 'Mac (Intel)',
};

export default {
  name: 'Printers',
  data() {
    return {
      summary: null,
      agents: [],
      jobs: [],
      job: null,
      showJob: false,
      jobLoading: false,
      loading: false,
      error: null,
      refreshedAt: null,
      timer: null,
      outletId: null,
      status: null,
      orderSearch: '',
      page: 1,
      perPage: 50,
      totalJobs: 0,
      statusOptions: [
        { value: null, text: 'All results' },
        { value: 'printed', text: 'Printed' },
        { value: 'failed', text: 'Failed' },
        { value: 'fetched', text: 'No report' },
      ],
      outletFields: [
        { key: 'outlet_name', label: 'Store' },
        { key: 'agents', label: 'Computers' },
        { key: 'online', label: 'Online' },
        { key: 'versions', label: 'Versions' },
        { key: 'last_print_at', label: 'Last print' },
        { key: 'printed_24h', label: 'Printed 24h' },
        { key: 'failed_24h', label: 'Failed 24h' },
      ],
      agentFields: [
        { key: 'outlet', label: 'Store' },
        { key: 'hostname', label: 'Computer' },
        { key: 'os', label: 'System' },
        { key: 'version', label: 'Version' },
        { key: 'printer', label: 'Printer' },
        { key: 'online', label: 'Last seen' },
        { key: 'last_print', label: 'Last print' },
      ],
      jobFields: [
        { key: 'created_at', label: 'Time' },
        { key: 'outlet', label: 'Store' },
        { key: 'invoice', label: 'Order' },
        { key: 'agent', label: 'Computer' },
        { key: 'language', label: 'Language' },
        { key: 'status', label: 'Result' },
        { key: 'bytes', label: 'Size' },
        { key: 'timings', label: 'Fetch / send / finish' },
        { key: 'error', label: 'Error' },
      ],
    };
  },
  computed: {
    outlets() {
      return this.summary ? this.summary.outlets : [];
    },
    outletOptions() {
      return [
        { value: null, text: 'All stores' },
        ...this.outlets.map(outlet => ({ value: outlet.outlet_id, text: outlet.outlet_name })),
      ];
    },
    jobRows() {
      const job = this.job;
      return [
        { label: 'Time', value: this.absolute(job.created_at) },
        { label: 'Reported', value: job.reported_at ? this.absolute(job.reported_at) : '-' },
        { label: 'Store', value: job.outlet ? job.outlet.name : '-' },
        { label: 'Order', value: job.invoice || `#${job.order_id}` },
        { label: 'Computer', value: job.agent ? job.agent.hostname : 'old agent (no report)' },
        { label: 'System', value: job.agent ? this.osName(job.agent.os) : '-' },
        { label: 'Agent version', value: job.agent_version || '-' },
        { label: 'Language', value: job.language || '-' },
        { label: 'Result', value: this.statusLabel(job.status) },
        { label: 'Failed at', value: job.stage || '-' },
        { label: 'Size', value: this.kb(job.bytes) },
        { label: 'Render', value: this.ms(job.render_ms) },
        { label: 'Fetch', value: this.ms(job.fetch_ms) },
        { label: 'Send', value: this.ms(job.send_ms) },
        { label: 'Finish', value: `${job.finish || '-'} ${this.ms(job.finish_ms)}` },
        { label: 'Error', value: job.error || '-' },
      ];
    },
  },
  mounted() {
    this.refresh();
    this.timer = setInterval(this.refresh, 30000);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    api(path, params) {
      return axios.get(`${this.newAppApiUrl}/staff/print-monitor/${path}`, { params });
    },
    refresh() {
      this.loading = true;
      Promise.all([this.loadSummary(), this.loadAgents(), this.loadJobs()])
        .then(() => {
          this.error = null;
          this.refreshedAt = new Date();
        })
        .catch(error => {
          this.error = this.message(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadSummary() {
      return this.api('summary').then(({ data }) => {
        this.summary = data;
      });
    },
    loadAgents() {
      return this.api('agents', { outlet_id: this.outletId || undefined }).then(({ data }) => {
        this.agents = data.data;
      });
    },
    loadJobs() {
      return this.api('jobs', {
        outlet_id: this.outletId || undefined,
        status: this.status || undefined,
        order: this.orderSearch || undefined,
        page: this.page,
        per_page: this.perPage,
      }).then(({ data }) => {
        this.jobs = data.data;
        this.totalJobs = data.total;
      });
    },
    loadJobsFirstPage() {
      this.page = 1;
      this.loadAgents();
      this.loadJobs().catch(error => {
        this.error = this.message(error);
      });
    },
    loadJobsPage(page) {
      this.page = page;
      this.loadJobs().catch(error => {
        this.error = this.message(error);
      });
    },
    selectOutlet(outlet) {
      this.outletId = outlet ? outlet.outlet_id : null;
      this.loadJobsFirstPage();
    },
    outletRowClass(item) {
      return item && item.outlet_id === this.outletId ? 'table-active' : 'clickable';
    },
    outletName(id) {
      const outlet = this.outlets.find(row => row.outlet_id === id);
      return outlet ? outlet.outlet_name : `#${id}`;
    },
    openJob(item) {
      this.showJob = true;
      this.jobLoading = true;
      this.job = null;
      this.api(`jobs/${item.id}`)
        .then(({ data }) => {
          this.job = data.data || data;
        })
        .catch(error => {
          this.error = this.message(error);
          this.showJob = false;
        })
        .finally(() => {
          this.jobLoading = false;
        });
    },
    message(error) {
      const data = error.response && error.response.data;
      if (data && data.message) {
        return `Could not load printer data: ${data.message}`;
      }
      return `Could not load printer data: ${error.message}`;
    },
    osName(os) {
      return OS_NAMES[os] || os || '-';
    },
    statusLabel(status) {
      return { printed: 'printed', failed: 'failed', fetched: 'no report' }[status] || status;
    },
    statusVariant(status) {
      return { printed: 'success', failed: 'danger', fetched: 'warning' }[status] || 'secondary';
    },
    relative(date) {
      return date ? this.$moment(date).fromNow() : '-';
    },
    absolute(date) {
      return date ? this.$moment(date).format('D MMM YYYY, h:mm:ss a') : '-';
    },
    kb(bytes) {
      return bytes ? `${Math.round(bytes / 1024)} KB` : '-';
    },
    ms(value) {
      return value === null || value === undefined ? '' : `${value} ms`;
    },
    timings(job) {
      return [job.fetch_ms, job.send_ms, job.finish_ms]
        .map(value => (value === null || value === undefined ? '-' : value))
        .join(' / ');
    },
  },
};
</script>

<style lang="scss" scoped>
.printers-page {
  padding: 24px;
}

.filter-select {
  width: 200px;
}

.error-cell {
  max-width: 280px;
}

.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;

  &.online {
    background: #28a745;
  }

  &.offline {
    background: #adb5bd;
  }
}

.log-tail {
  max-height: 360px;
  overflow: auto;
  background: #f5f5f5;
  padding: 12px;
  font-size: 12px;
  white-space: pre-wrap;
}

::v-deep .clickable,
.clickable-rows ::v-deep tbody tr {
  cursor: pointer;
}
</style>
